import { useEffect, useState } from 'react';
import { useTheme } from 'next-themes';
import { useAuth } from '@/hooks/auth.hook';
import { useRootSelector } from '@/hooks/selector.hook';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger } from '../ui/dropdown-menu';
import Logo from '@/assets/images/LOGO.png';
import LogoLight from '@/assets/images/logo-light.png';
import { IoIosLogOut } from 'react-icons/io';
import ThemeToggle from '@/components/Helper/ThemeToggle';

export default function KYCHeader() {
  const { resolvedTheme } = useTheme();
  const { logout, fetchProfile } = useAuth();
  const user = useRootSelector((state) => state.auth.user);
  const token = useRootSelector((state) => state.auth.token);
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    if (token && !user) {
      fetchProfile();
    }
  }, [token, user, fetchProfile]);

  const handleLogout = async () => {
    setIsOpen(false);
    await logout();
  };

  return (
    <header className="w-full">
      <div className="flex items-center justify-between px-[2rem] py-[2rem]">
        {/* Logo bên trái */}
        <div className="flex items-center gap-[1rem]">
          <img
            src={resolvedTheme === 'dark' ? Logo : LogoLight}
            alt="nezenx"
            className="h-auto w-[10rem] lg:w-[15rem]"
          />
          <div className="lg:hidden">
            <ThemeToggle />
          </div>
        </div>

        {/* Desktop: User profile và logout button bên phải */}
        <div className="hidden lg:flex items-center gap-[1.5rem]">
          {/* User Profile */}
          <div className="flex items-center gap-[1.2rem]">
            <Avatar className="w-[4rem] h-[4rem]">
              <AvatarImage src={user?.avatar} alt={user?.email} />
              <AvatarFallback>
                {user?.firstName?.[0] || 'U'}
                {user?.lastName?.[0] || ''}
              </AvatarFallback>
            </Avatar>
            <div className="flex flex-col">
              <div className="text-[1.5rem] font-medium dark:text-[#FFFFFF] text-[#000000]">
                {user?.firstName} {user?.lastName}
              </div>
              <div className="text-[1.2rem] dark:text-[#BFBFBF] text-[#7A7A7A]">
                {user?.roles?.length! > 0 ? user?.roles?.[0]?.name : 'User'}
              </div>
            </div>
          </div>

          <ThemeToggle />

          {/* Logout Button */}
          <button
            onClick={handleLogout}
            className="flex items-center gap-[0.8rem] px-[1.5rem] py-[1rem] rounded-[1.2rem] bg-[#57E8BA] dark:bg-[#57E8BA] hover:bg-[#4DD4A8] dark:hover:bg-[#4DD4A8] transition-colors"
          >
            <IoIosLogOut className="w-[1.8rem] h-[1.8rem] text-[#000000]" />
            <span className="text-[1.6rem] font-semibold text-[#000000]">Logout</span>
          </button>
        </div>

        {/* Mobile: Dropdown với logo */}
        <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
          <DropdownMenuTrigger asChild>
            <button className="lg:hidden flex items-center">
              <Avatar className="w-[4rem] h-[4rem]">
                <AvatarImage src={user?.avatar} alt={user?.email} />
                <AvatarFallback>
                  {user?.firstName?.[0] || 'U'}
                  {user?.lastName?.[0] || ''}
                </AvatarFallback>
              </Avatar>
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="end"
            className="w-[20rem] bg-[#161B19] border border-[#717171] rounded-[1.2rem] p-[1.5rem]"
          >
            {/* User Info */}
            <div className="flex flex-col gap-[1rem] mb-[1.5rem] pb-[1.5rem] border-b border-[#717171]">
              <div className="flex items-center gap-[1.2rem]">
                <Avatar className="w-[4rem] h-[4rem]">
                  <AvatarImage src={user?.avatar} alt={user?.email} />
                  <AvatarFallback>
                    {user?.firstName?.[0] || 'U'}
                    {user?.lastName?.[0] || ''}
                  </AvatarFallback>
                </Avatar>
                <div className="flex flex-col">
                  <div className="text-[1.5rem] font-medium text-[#FFFFFF]">
                    {user?.firstName} {user?.lastName}
                  </div>
                  <div className="text-[1.2rem] text-[#BFBFBF]">
                    {user?.roles?.length! > 0 ? user?.roles?.[0]?.name : 'User'}
                  </div>
                </div>
              </div>
            </div>

            {/* Logout Button */}
            <button
              onClick={handleLogout}
              className="w-full flex items-center justify-center gap-[0.8rem] px-[1.5rem] py-[1rem] rounded-[1.2rem] bg-[#57E8BA] hover:bg-[#4DD4A8] transition-colors"
            >
              <IoIosLogOut className="w-[1.8rem] h-[1.8rem] text-[#000000]" />
              <span className="text-[1.6rem] font-semibold text-[#000000]">Logout</span>
            </button>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Đường line ở dưới */}
      <div className="w-full h-[0.1rem] bg-[#717171]" />
    </header>
  );
}
